import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { Home, Search, Heart, LogOut, MapPin, Sparkles, MessageSquare, FileSignature, ClipboardCheck, Map as MapIcon, List as ListIcon, Menu } from 'lucide-react';
import { useCompare } from '@/hooks/useCompare';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ViewportMenuItems from '@/components/ViewportMenuItems';
import { ReportBugDialog } from '@/components/ReportBug';
import { getCurrentPosition } from '@/hooks/useNative';
import NotificationBell from '@/components/NotificationBell';
import SecurityModal from '@/components/SecurityModal';
import PropertyMap from '@/components/PropertyMap';
import BackButton from '@/components/BackButton';
import { PropertyCard } from '@/components/tenant/PropertyCard';

const DEFAULT_FILTERS = { city: '', property_type: 'all', min_bedrooms: '', has_discount: false };

export default function TenantDashboard() {
  const { user, logout } = useContext(AuthContext);
  const compare = useCompare();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showBugDialog, setShowBugDialog] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const fetchProperties = useCallback(async (appliedFilters) => {
    const f = appliedFilters || filters;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.city) params.append('city', f.city);
      if (f.property_type && f.property_type !== 'all') params.append('property_type', f.property_type);
      if (f.min_bedrooms) params.append('min_bedrooms', f.min_bedrooms);
      if (f.has_discount) params.append('has_discount', 'true');
      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSavedProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/saved-properties`);
      setSavedProperties(response.data);
      setSavedIds(new Set(response.data.map(p => p.id)));
    } catch (error) {
      log.error('Failed to fetch saved properties:', error);
    }
  }, []);

  useEffect(() => {
    fetchProperties(DEFAULT_FILTERS);
    fetchSavedProperties();
  }, [fetchProperties, fetchSavedProperties]);

  const handleSaveProperty = useCallback(async (propertyId) => {
    try {
      if (savedIds.has(propertyId)) {
        await axios.delete(`${API}/saved-properties/${propertyId}`);
        toast.success('Property removed from saved');
        setSavedIds(prev => {
          const next = new Set(prev);
          next.delete(propertyId);
          return next;
        });
      } else {
        await axios.post(`${API}/saved-properties/${propertyId}`, {});
        toast.success('Property saved successfully');
        setSavedIds(prev => new Set([...prev, propertyId]));
      }
      fetchSavedProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  }, [savedIds, fetchSavedProperties]);

  const handleNearMe = useCallback(async () => {
    const pos = await getCurrentPosition();
    if (!pos) {
      toast.error('Could not access your location');
      return;
    }
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.lat}&lon=${pos.lng}&zoom=10`);
      const data = await r.json();
      const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || '';
      if (city) {
        const newFilters = { ...filters, city };
        setFilters(newFilters);
        fetchProperties(newFilters);
        setViewMode('map');
        toast.success(`Showing properties near ${city}`);
      } else {
        toast.error('Could not determine your city');
      }
    } catch {
      toast.error('Location lookup failed');
    }
  }, [filters, fetchProperties]);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    fetchProperties(DEFAULT_FILTERS);
  }, [fetchProperties]);

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <BackButton fallback="/" />
            <div className="flex items-center gap-2 sm:gap-3 min-w-0" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <Home className="w-6 h-6 sm:w-7 sm:h-7 text-primary shrink-0" />
              <span className="text-lg sm:text-2xl font-bold tracking-tight text-primary truncate">PrimePocket</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <NotificationBell />
            <SecurityModal />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="tenant-menu-trigger" variant="outline" size="sm" className="rounded-full gap-2">
                  <Menu className="w-4 h-4" />
                  <span className="hidden sm:inline">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="tenant-menu">
                <DropdownMenuLabel className="flex flex-col items-start">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-leases" onClick={() => navigate('/leases')}>
                  <FileSignature className="w-4 h-4 mr-2" /> My Applications & Leases
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-compare" onClick={() => navigate('/compare')}>
                  <ClipboardCheck className="w-4 h-4 mr-2" /> Compare Properties
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate('/messages')}>
                  <MessageSquare className="w-4 h-4 mr-2" /> Messages
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-forms" onClick={() => navigate('/forms')}>
                  <ClipboardCheck className="w-4 h-4 mr-2" /> Forms
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-report-bug" onClick={() => setShowBugDialog(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" /> Report a bug
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
                <ViewportMenuItems />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <ReportBugDialog open={showBugDialog} onOpenChange={setShowBugDialog} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10 lg:py-12">
        <Tabs defaultValue="browse" className="w-full">
          <div className="flex items-center justify-between mb-6 sm:mb-8 overflow-x-auto">
            <TabsList className="bg-muted">
              <TabsTrigger data-testid="browse-tab" value="browse" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm">
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Browse Properties</span>
                <span className="sm:hidden">Browse</span>
              </TabsTrigger>
              <TabsTrigger data-testid="saved-tab" value="saved" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm">
                <Heart className="w-4 h-4 sm:mr-2" />
                Saved ({savedProperties.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="browse">
            <Card className="p-4 sm:p-6 mb-6 sm:mb-8 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-base sm:text-lg font-medium">Filter Properties</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Input
                  data-testid="filter-city-input"
                  placeholder="City"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                />
                <Select value={filters.property_type} onValueChange={(v) => setFilters({ ...filters, property_type: v })}>
                  <SelectTrigger data-testid="filter-type-select"><SelectValue placeholder="Property Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  data-testid="filter-bedrooms-input"
                  type="number"
                  placeholder="Min Bedrooms"
                  value={filters.min_bedrooms}
                  onChange={(e) => setFilters({ ...filters, min_bedrooms: e.target.value })}
                />
                <div className="flex gap-2">
                  <Button
                    data-testid="filter-apply-btn"
                    onClick={() => fetchProperties(filters)}
                    className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover"
                  >
                    Apply Filters
                  </Button>
                  <Button data-testid="filter-near-me-btn" onClick={handleNearMe} variant="outline" className="rounded-full">
                    <MapPin className="w-4 h-4 mr-2" />
                    Near Me
                  </Button>
                  <Button data-testid="filter-reset-btn" onClick={handleResetFilters} variant="outline" className="rounded-full">
                    Reset
                  </Button>
                </div>
              </div>
              <div className="mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="filter-discount-checkbox"
                    type="checkbox"
                    checked={filters.has_discount}
                    onChange={(e) => {
                      const nf = { ...filters, has_discount: e.target.checked };
                      setFilters(nf);
                      fetchProperties(nf);
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Show only discounted properties</span>
                </label>
              </div>
            </Card>

            {loading && <div className="text-center py-20">Loading properties...</div>}
            {!loading && properties.length === 0 && (
              <Card className="p-20 text-center border-2 border-dashed">
                <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">No Properties Found</h3>
                <p className="text-muted-foreground">Try adjusting your filters</p>
              </Card>
            )}
            {!loading && properties.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-muted-foreground">
                    {properties.length} propert{properties.length === 1 ? 'y' : 'ies'} found
                  </div>
                  <div className="inline-flex rounded-full border border-border p-1 bg-muted">
                    <button
                      data-testid="view-list-btn"
                      onClick={() => setViewMode('list')}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center gap-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <ListIcon className="w-4 h-4" />List
                    </button>
                    <button
                      data-testid="view-map-btn"
                      onClick={() => setViewMode('map')}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center gap-2 transition-colors ${viewMode === 'map' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <MapIcon className="w-4 h-4" />Map
                    </button>
                  </div>
                </div>

                {viewMode === 'map' ? (
                  <PropertyMap properties={properties} height="700px" />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {properties.map((property) => (
                      <PropertyCard
                        key={property.id}
                        property={property}
                        isSaved={savedIds.has(property.id)}
                        onToggleSave={handleSaveProperty}
                        compare={compare}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="saved">
            {savedProperties.length === 0 ? (
              <Card className="p-20 text-center border-2 border-dashed">
                <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">No Saved Properties</h3>
                <p className="text-muted-foreground">Start saving properties you like</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {savedProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    isSaved={true}
                    onToggleSave={handleSaveProperty}
                    compare={compare}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
